import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Note: For admin-only authentication in a non-profit site, localStorage is acceptable.
// For higher security needs, consider httpOnly cookies with backend session management.

export function AuthProvider({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.valid) {
        setIsAdmin(true);
        setAdminUsername(response.data.username);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
    } catch (error) {
      // Token invalid - clear it
      localStorage.removeItem("admin_token");
      setIsAdmin(false);
      setAdminUsername("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if there's a valid token on mount
    const token = localStorage.getItem("admin_token");
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  const login = useCallback(async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, username: user } = response.data;
      
      localStorage.setItem("admin_token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      setIsAdmin(true);
      setAdminUsername(user);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || "Login failed" 
      };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    delete axios.defaults.headers.common["Authorization"];
    setIsAdmin(false);
    setAdminUsername("");
  }, []);

  // Set up axios interceptor to add token to all requests - runs once on mount
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ isAdmin, adminUsername, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
